/* eslint-disable max-lines */
import { Alert, AlertDescription } from "@altimateai/lego";
import { QueryHeader } from "./Query";
import { useEffect } from "react";
import { Paginate } from "../../components/paginate";
import { DataTable } from "@/components/tables";
import { useQuery } from "@tanstack/react-query";
import { getAllQueryGroups } from "../../helpers/apis";
import { downloadAllQueryGroupsCSV } from "@/helpers/apis";
import { getQueryFilters } from "@/helpers/apis";
import { RelativeComponentLoader } from "../../components/loader";
import { GET_ALL_QUERY_GROUPS } from "./constants";
import { ColorInsightTags, FilterTagWrapper } from "../../components/tags";
import { cn } from "@/lib/utils";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DateRange } from "../../components/dateRange";
import dayjs from "dayjs";
import QueryTextWithHover from "./QueryText";
import InsightLabel from "./InsightLabel";
import { formatNumber } from "@/uiCore";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { useAppState } from "@/modules/app/useAppContext";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { FilterApplicableFor } from "@/components/tags/types";
import SavedFilters from "@/components/tags/SavedFilters";
import usePaginate from "@/components/paginate/usePaginate";
import { DownloadTable } from "@/components/downloadTable";
import { getInstanceName } from "@/helpers/utils";
import {
  FilterEntityType,
  QueryFilters,
  QueryGroup,
  QueryGroupsFilters,
  QueryTags,
} from "@/pages/query/types";
import {
  TableHeaderType,
  FilterType,
  type APIFilterParams,
  createAPIDropdownFilter,
  parseSortDirection,
} from "@/components/tables/types";
import { Stack } from "@altimateai/lego";
import { useBreadcrumb } from "@/components/breadcrumbs";

const Groups = () => {
  useBreadcrumb("Groups", {
    defaultParents: [{ label: "Code", pathname: "/query" }],
    truncateTo: "/query",
  });
  const { currency, datastores } = useAppState();
  const { queryHash } = useParams<{ queryHash?: string }>();
  const [searchParams] = useSearchParams();
  const { deleteSearchParamsByKeyValue, updateSearchParamsByKey } = useFiltersBySearchParams();
  const { page, pageSize, setPage, updatePageSize, resetPage } = usePaginate();
  const sortAttribute = searchParams.get("sort_key") || "annualized_cost";
  const sortOrder = parseSortDirection(searchParams.get("sort_order"), "desc");
  const tagFilter = searchParams.getAll("tags") || [];
  const userFilter = searchParams.getAll("users") || [];
  const warehouseFilter = searchParams.getAll("warehouses") || [];
  const instanceFilter = searchParams.getAll("instance") || [];
  const navigate = useNavigate();

  const { startDate, endDate } = useDateFromSearchParams(
    dayjs().subtract(28, "day").toDate(),
    dayjs().toDate()
  );

  const getFilteredParams = (): QueryGroupsFilters => {
    const params: QueryGroupsFilters = {
      page: page + 1,
      size: pageSize,
      sort_key: sortAttribute,
      sort_order: (sortOrder || "desc") as string,
      start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
      end_date: getDateByEndOfDayWithoutTimeoffset(endDate)!.toISOString(),
    };
    if (queryHash) params.query_hash = queryHash;
    if (tagFilter.length > 0) params.tags = tagFilter;
    if (userFilter.length > 0) params.users = userFilter;
    if (warehouseFilter.length > 0) params.warehouses = warehouseFilter;
    if (instanceFilter.length > 0) params.instance_ids = instanceFilter;
    return params;
  };

  const getRowUrl = (group: QueryGroup) =>
    `/query/groups/${group.query_hash}?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`;

  const getRowState = (group: QueryGroup) => ({ entityName: group.query_hash });

  const { data: groups, isFetching: isLoading } = useQuery({
    queryKey: [
      GET_ALL_QUERY_GROUPS,
      sortAttribute,
      sortOrder,
      page,
      pageSize,
      ...tagFilter,
      ...userFilter,
      ...warehouseFilter,
      ...instanceFilter,
      startDate,
      endDate,
    ],
    queryFn: () => getAllQueryGroups(getFilteredParams()),
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (queryHash && groups) {
      const group = groups.items.find(item => item.query_hash === queryHash);
      if (group) {
        navigate(getRowUrl(group));
      }
    }
  }, [groups, queryHash, navigate, startDate, endDate]);

  const handleDownloadClick = async (): Promise<void> => {
    await downloadAllQueryGroupsCSV(getFilteredParams());
  };

  // Type guard for filter change value
  const isFilterValue = (value: unknown): value is string | string[] => {
    if (typeof value === "string") return true;
    if (Array.isArray(value) && value.every(v => typeof v === "string")) return true;
    return false;
  };

  // Generic filter change handler factory
  const createFilterChangeHandler =
    (key: "warehouses" | "users" | "tags" | "instance") => (value: unknown) => {
      if (!isFilterValue(value)) return;
      const normalizedValue = Array.isArray(value) ? value : [value];
      updateSearchParamsByKey({ [key]: normalizedValue });
    };

  const handleWarehouseFilterChange = createFilterChangeHandler("warehouses");
  const handleUserFilterChange = createFilterChangeHandler("users");
  const handleInsightsFilterChange = createFilterChangeHandler("tags");
  const handleInstanceFilterChange = createFilterChangeHandler("instance");

  // Factory function for API filter dropdowns
  const createFilterApiFunction =
    (entityType: FilterEntityType) =>
    async ({ prefix, page, size }: APIFilterParams): Promise<QueryFilters | undefined> =>
      getQueryFilters({
        entity_type: entityType,
        prefix,
        page,
        size,
        filter_type: "query_group",
      });

  const getWarehousesFilter = createFilterApiFunction(FilterEntityType.WAREHOUSES);
  const getUsersFilter = createFilterApiFunction(FilterEntityType.USERS);
  const getTagsFilter = createFilterApiFunction(FilterEntityType.TAGS);

  const header: TableHeaderType<QueryGroup>[] = [
    {
      id: 1,
      label: "Group",
      width: "20%",
      uid: "query_group",
      render: item => (
        <div className="al-break-all">
          <QueryTextWithHover queryHash={item.query_hash} queryText={item.query_hash} />
        </div>
      ),
    },
    {
      id: 2,
      label: "Total run",
      uid: "total_run",
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
        sortParamValue: "count",
      },
      render: item => <div className="al-break-words">{item.total_run}</div>,
    },
    {
      id: 3,
      label: "Avg. execution time",
      uid: "avg_exec_time",
      width: "10%",
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
        sortParamValue: "avg_execution_time",
      },
      render: item => <div className="al-break-words">{item.avg_exec_time}</div>,
    },
    {
      id: 4,
      label: "Avg. cost",
      uid: "avg_cost",
      render: item => (
        <div className="al-break-words">{formatNumber(item.avg_cost, { currency })}</div>
      ),
    },
    {
      id: 5,
      label: "Total cost",
      uid: "total_cost",
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
      render: item => (
        <div className="al-break-words">{formatNumber(item.total_cost, { currency })}</div>
      ),
    },
    {
      id: 6,
      label: "Annualized cost",
      uid: "annualized_cost",
      width: "8%",
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
      render: item => (
        <div className="al-break-words">{formatNumber(item.annualized_cost, { currency })}</div>
      ),
    },
    {
      id: 7,
      label: "Warehouses",
      uid: "warehouses",
      width: "10%",
      filter: createAPIDropdownFilter({
        filterType: FilterType.API_DROPDOWN,
        value: warehouseFilter,
        searchKey: "warehouses",
        apiFunction: getWarehousesFilter,
        pageSize: 50,
        seeMore: true,
        onChange: handleWarehouseFilterChange,
        extractorFunction: data => {
          return (
            data?.warehouses?.map((warehouse: string) => ({
              label: warehouse,
              value: warehouse,
            })) || []
          );
        },
      }),
      render: item => (
        <div className="al-break-words lines-2">{item.warehouses?.join(", ") ?? ""}</div>
      ),
    },
    {
      id: 8,
      label: "Users",
      uid: "users",
      width: "8%",
      filter: createAPIDropdownFilter({
        filterType: FilterType.API_DROPDOWN,
        searchKey: "users",
        value: userFilter,
        apiFunction: getUsersFilter,
        pageSize: 50,
        seeMore: true,
        onChange: handleUserFilterChange,
        extractorFunction: data => {
          return (
            data?.users?.map((user: string) => ({
              label: user,
              value: user,
            })) || []
          );
        },
      }),
      render: item => <div className="al-break-words lines-2">{item.users?.join(", ") ?? ""}</div>,
    },
    {
      id: 9,
      label: "Insights",
      uid: "tags",
      width: "15%",
      filter: createAPIDropdownFilter({
        filterType: FilterType.API_DROPDOWN,
        searchKey: "tags",
        value: tagFilter,
        apiFunction: getTagsFilter,
        pageSize: 50,
        seeMore: true,
        onChange: handleInsightsFilterChange,
        extractorFunction: data => {
          return (
            data?.tags?.map((t: QueryTags) => ({
              label: <InsightLabel tag={t} />,
              value: t.name,
              showTooltip: false,
            })) || []
          );
        },
      }),
      render: item => <ColorInsightTags tags={item.tags} />,
    },
    {
      id: 10,
      label: "Instance",
      uid: "instance_id",
      width: "10%",
      filter: {
        filterType: FilterType.DROPDOWN,
        value: instanceFilter,
        searchKey: "instance",
        options: (datastores || []).map(datastore => ({
          label: datastore.name,
          value: datastore.instance_id || "",
        })),
        onChange: handleInstanceFilterChange,
      },
      render: item => {
        return (
          <div className={cn("al-break-words", "lines-2")}>
            {getInstanceName(item.instance_id || "", datastores || [])}
          </div>
        );
      },
    },
  ];

  if (isLoading) return <RelativeComponentLoader label="Loading query groups" />;
  if (!groups) {
    return (
      <Alert variant="destructive">
        <AlertDescription>No data for query group found</AlertDescription>
      </Alert>
    );
  }

  return (
    <Stack direction="column">
      <QueryHeader />
      <div className="divider" />
      <div className="al-p-3 al-bg-white al-rounded-b-lg">
        <Stack direction="column" gap="md">
          <Stack direction="row" gap="md" align="center">
            <div className="spacer" />
            <DateRange
              startDate={startDate}
              endDate={endDate}
              availableStaticRanges={["Last day", "Last 7 days", "Last 28 days"]}
              disableCalendarSelection
              onDateRangeSelect={(s, e) => {
                updateSearchParamsByKey({
                  start_date: s?.toISOString(),
                  end_date: e?.toISOString(),
                });
              }}
            />
            <SavedFilters applicableFor={FilterApplicableFor.QUERY_GROUP} />
            <DownloadTable handleDownload={handleDownloadClick} />
            <div id="columnpicker-container" />
          </Stack>
          <Stack direction="row" gap="sm" wrap>
            <FilterTagWrapper
              applicableFor={FilterApplicableFor.QUERY_GROUP}
              filters={{
                tagFilter: {
                  label: "Tags",
                  filterStr: tagFilter,
                  searchKey: "tags",
                },
                userFilter: {
                  label: "Users",
                  filterStr: userFilter,
                  searchKey: "users",
                },
                warehouseFilter: {
                  label: "Warehouse",
                  filterStr: warehouseFilter,
                  searchKey: "warehouses",
                },
                instanceFilter: {
                  label: "Instance",
                  filterStr: instanceFilter
                    .map(instance => getInstanceName(instance, datastores || []))
                    .filter((v): v is string => v !== undefined),
                  searchKey: "instance",
                  onclose: (removedFilter?: string) => {
                    deleteSearchParamsByKeyValue({
                      instance:
                        (datastores || []).find(datastore => datastore.name === removedFilter)
                          ?.instance_id ?? undefined,
                    });
                  },
                },
              }}
            />
          </Stack>
          <DataTable
            applicableFor={FilterApplicableFor.QUERY_GROUP}
            data={groups}
            header={header}
            resetPage={resetPage}
            getRowUrl={getRowUrl}
            getRowState={getRowState}
            columnPickerPortal="columnpicker-container"
            defaultSort={{ sortParamValue: "annualized_cost", order: "desc" }}
          />

          <Paginate
            itemCount={groups.total}
            page={page}
            pageSize={pageSize}
            numPages={groups.pages}
            onPageClick={setPage}
            showPageSize
            onPageSizeChange={updatePageSize}
          />
        </Stack>
      </div>
    </Stack>
  );
};

export { Groups };
